import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import UniqueQueueSelect from "../QueueSelect/uniqueQueueSelect";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
	Grid,
	FormControl,
	MenuItem,
	InputLabel,
	Select,
} from "@material-ui/core";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const TelegramSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muy corto!")
		.max(50, "Muy largo!")
		.required("Requerido"),
	telegramApiId: Yup.string()
		.matches(/^\d+$/, "El API ID debe ser numérico")
		.required("API ID es requerido"),
	telegramApiHash: Yup.string()
		.min(32, "El API Hash debe tener al menos 32 caracteres")
		.required("API Hash es requerido"),
});

const TelegramModal = ({ open, onClose, telegramId }) => {
	const classes = useStyles();
	const initialState = {
		name: "",
		greetingMessage: "",
		complationMessage: "",
		outOfHoursMessage: "",
		isDefault: false,
		telegramApiId: "",
		telegramApiHash: "",
		expiresTicket: 0,
		messageExpiresTicket: "",
		flowId: 0,
	};
	const [telegram, setTelegram] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [selectedQueueId, setSelectedQueueId] = useState(null);
	const [flows, setFlows] = useState([]);

	useEffect(() => {
		const fetchSession = async () => {
			if (!telegramId) return;

			try {
				const { data } = await api.get(`telegram-connections/${telegramId}`);
				setTelegram(data);

				const telegramQueueIds = data.queues?.map((queue) => queue.id);
				setSelectedQueueId(data.maxUseBotQueueId);
				setSelectedQueueIds(telegramQueueIds);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [telegramId]);

	const handleSaveTelegram = async (values) => {
		const telegramData = {
			...values,
			queueIds: selectedQueueIds,
			maxUseBotQueueId: selectedQueueId,
		};
		delete telegramData["queues"];
		delete telegramData["session"];

		try {
			if (telegramId) {
				await api.put(`/telegram-connections/${telegramId}`, telegramData);
			} else {
				await api.post("/telegram-connections", telegramData);
			}
			toast.success("Conexión de Telegram guardada exitosamente!");
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setTelegram(initialState);
		setSelectedQueueIds([]);
		setSelectedQueueId(null);
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{telegramId
						? "Editar Conexión de Telegram"
						: "Agregar Conexión de Telegram"}
				</DialogTitle>
				<Formik
					initialValues={telegram}
					enableReinitialize={true}
					validationSchema={TelegramSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveTelegram(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Grid container spacing={2}>
										<Grid item xs={12} md={6}>
											<Field
												as={TextField}
												label="Nombre"
												name="name"
												error={touched.name && Boolean(errors.name)}
												helperText={touched.name && errors.name}
												variant="outlined"
												margin="dense"
												fullWidth
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={
													<Field
														as={Switch}
														name="isDefault"
														color="primary"
														checked={values.isDefault}
													/>
												}
												label="Conexión por defecto"
											/>
										</Grid>
									</Grid>
								</div>

								<div className={classes.multFieldLine}>
									<Grid container spacing={2}>
										<Grid item xs={12} md={6}>
											<Field
												as={TextField}
												label="API ID de Telegram"
												name="telegramApiId"
												error={touched.telegramApiId && Boolean(errors.telegramApiId)}
												helperText={touched.telegramApiId && errors.telegramApiId}
												variant="outlined"
												margin="dense"
												fullWidth
												placeholder="123456789"
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<Field
												as={TextField}
												label="API Hash de Telegram"
												name="telegramApiHash"
												error={touched.telegramApiHash && Boolean(errors.telegramApiHash)}
												helperText={touched.telegramApiHash && errors.telegramApiHash}
												variant="outlined"
												margin="dense"
												fullWidth
												placeholder="abcdef123456789..."
											/>
										</Grid>
									</Grid>
								</div>

								<QueueSelect
									selectedQueueIds={selectedQueueIds}
									onChange={(values) => setSelectedQueueIds(values)}
								/>

								<Field
									as={TextField}
									label="Mensaje de saludo"
									name="greetingMessage"
									multiline
									minRows={5}
									fullWidth
									margin="dense"
									variant="outlined"
								/>

								<Field
									as={TextField}
									label="Mensaje de finalización"
									name="complationMessage"
									multiline
									minRows={5}
									fullWidth
									margin="dense"
									variant="outlined"
								/>

								<Field
									as={TextField}
									label="Mensaje fuera de horario"
									name="outOfHoursMessage"
									multiline
									minRows={5}
									fullWidth
									margin="dense"
									variant="outlined"
								/>

								<div className={classes.multFieldLine}>
									<Grid container spacing={2}>
										<Grid item xs={12} md={6}>
											<Field
												as={TextField}
												label="Tiempo de expiración del ticket (minutos)"
												name="expiresTicket"
												type="number"
												variant="outlined"
												margin="dense"
												fullWidth
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<Field
												as={TextField}
												label="Mensaje de expiración del ticket"
												name="messageExpiresTicket"
												variant="outlined"
												margin="dense"
												fullWidth
											/>
										</Grid>
									</Grid>
								</div>
							</DialogContent>
							<DialogActions>
								<Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
									Cancelar
								</Button>
								<Button
									type="submit"
									color="primary"
									variant="contained"
									disabled={isSubmitting}
									className={classes.btnWrapper}
								>
									{telegramId ? "Guardar" : "Agregar"}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default React.memo(TelegramModal);